import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SortByAlphaOutlinedIcon from '@mui/icons-material/SortByAlphaOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import type { HelpValue } from '../common/helpModel';
import type { TableColumnFilter, TableFilterMode, TableFilterOperator, TableSortDirection } from './hooks/useTableFilters';
import { perf } from '../../lib/perfLogger';
import { borderRadius, getSurfaceBorderColor, layoutGap } from '../../../theme/theme';

export const FILTER_OPERATOR_OPTIONS: Array<{ value: TableFilterOperator; label: string }> = [
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Does not equal' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' },
];

interface ColumnFilterMenuProps {
  columnLabel: string;
  filter: TableColumnFilter | undefined;
  uniqueValues: string[];
  valueCounts: Record<string, number>;
  onApplyValues: (selectedValues: string[] | null) => void;
  onApplyCondition: (operator: TableFilterOperator, value: string) => void;
  sortDirection: TableSortDirection | null;
  onSortAscending: () => void;
  onSortDescending: () => void;
  onClearSort: () => void;
  onClear: () => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
  popoverRef?: React.RefObject<HTMLDivElement | null>;
  previewOpen?: boolean;
  previewMode?: boolean;
  emphasizeTrigger?: boolean;
  help?: HelpValue;
  triggerVariant?: 'outlined' | 'inline';
  triggerSize?: number;
}

const displayValue = (value: string): string => (value === '' ? '(Empty)' : value);

const isValuelessOperator = (operator: TableFilterOperator): boolean =>
  operator === 'isEmpty' || operator === 'isNotEmpty';

const slugifyLabel = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const buildDefaultSelectedValues = (filter: TableColumnFilter | undefined, uniqueValues: string[]): string[] => {
  if (filter?.mode === 'values' && filter.selectedValues) {
    return filter.selectedValues;
  }
  return uniqueValues;
};

const ColumnFilterMenu: React.FC<ColumnFilterMenuProps> = ({
  columnLabel,
  filter,
  uniqueValues,
  valueCounts,
  onApplyValues,
  onApplyCondition,
  sortDirection,
  onSortAscending,
  onSortDescending,
  onClearSort,
  onClear,
  triggerRef,
  popoverRef,
  previewOpen = false,
  previewMode = false,
  emphasizeTrigger = false,
  help,
  triggerVariant = 'outlined',
  triggerSize,
}) => {
  const theme = useTheme();
  const surfaceBorderColor = getSurfaceBorderColor(theme);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [previewAnchorEl, setPreviewAnchorEl] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<TableFilterMode>(filter?.mode ?? 'values');
  const [valueSearch, setValueSearch] = useState('');
  const [draftSelectedValues, setDraftSelectedValues] = useState<string[]>(() => buildDefaultSelectedValues(filter, uniqueValues));
  const [draftOperator, setDraftOperator] = useState<TableFilterOperator>(filter?.operator ?? 'contains');
  const [draftConditionValue, setDraftConditionValue] = useState(filter?.value ?? '');
  const lastInteractionRef = useRef<{ key: string; at: number } | null>(null);
  const valueSearchInputRef = useRef<HTMLInputElement | null>(null);
  const filterSlug = slugifyLabel(columnLabel);

  const resolvedAnchorEl = previewOpen ? previewAnchorEl : anchorEl;
  const open = Boolean(resolvedAnchorEl);
  const active = Boolean(filter) || previewOpen;
  const isInteractive = !previewMode;
  const isInlineTrigger = triggerVariant === 'inline';
  const triggerDimension = triggerSize ?? (isInlineTrigger ? 32 : 22);
  const displayedValues = useMemo(
    () =>
      uniqueValues.filter((value) =>
        displayValue(value).toLowerCase().includes(valueSearch.trim().toLowerCase())
      ),
    [uniqueValues, valueSearch]
  );

  useEffect(() => {
    if (!previewOpen) {
      setPreviewAnchorEl(null);
      return;
    }

    let frameId = 0;
    const syncPreviewAnchor = () => {
      setPreviewAnchorEl(triggerRef?.current ?? null);
    };

    frameId = window.requestAnimationFrame(syncPreviewAnchor);
    window.addEventListener('resize', syncPreviewAnchor);
    perf.log('QF-FilterMenu', 'wizard preview requested', {
      columnLabel,
      previewMode,
      hasTriggerRef: Boolean(triggerRef?.current),
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', syncPreviewAnchor);
    };
  }, [columnLabel, previewMode, previewOpen, triggerRef]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setMode(filter?.mode ?? 'values');
    setDraftSelectedValues(buildDefaultSelectedValues(filter, uniqueValues));
    setDraftOperator(filter?.operator ?? 'contains');
    setDraftConditionValue(filter?.value ?? '');
    setValueSearch('');
    perf.log('QF-FilterMenu', 'menu opened', {
      columnLabel,
      previewOpen,
      previewMode,
      isInteractive,
      filterMode: filter?.mode ?? null,
      operator: filter?.operator ?? null,
      selectedValues: filter?.selectedValues?.length ?? null,
      uniqueValues: uniqueValues.length,
    });
  }, [columnLabel, filter, isInteractive, open, previewMode, previewOpen, uniqueValues]);

  useEffect(() => {
    if (!open || previewOpen || mode !== 'values') {
      return;
    }

    const focusId = window.setTimeout(() => {
      valueSearchInputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusId);
    };
  }, [mode, open, previewOpen]);

  const allDisplayedSelected =
    displayedValues.length > 0 && displayedValues.every((value) => draftSelectedValues.includes(value));
  const someDisplayedSelected =
    displayedValues.some((value) => draftSelectedValues.includes(value)) && !allDisplayedSelected;

  const toggleValue = (value: string) => {
    setDraftSelectedValues((previous) => {
      const next = previous.includes(value)
        ? previous.filter((existing) => existing !== value)
        : [...previous, value];

      perf.log('QF-FilterMenu', 'value toggled', {
        columnLabel,
        value,
        wasSelected: previous.includes(value),
        nextSelectedValueCount: next.length,
        nextSelectedValues: next,
        uniqueValues: uniqueValues.length,
      });

      return next;
    });
  };

  const shouldSkipDuplicateInteraction = (key: string) => {
    const now = performance.now();
    const last = lastInteractionRef.current;

    if (last && last.key === key && now - last.at < 250) {
      perf.log('QF-FilterMenu', 'duplicate interaction skipped', {
        columnLabel,
        key,
        elapsedMs: Math.round(now - last.at),
      });
      return true;
    }

    lastInteractionRef.current = { key, at: now };
    return false;
  };

  const handleValuePointerToggle = (event: React.SyntheticEvent, value: string, source: string) => {
    event.preventDefault();
    event.stopPropagation();

    const interactionKey = `value:${value}`;
    if (shouldSkipDuplicateInteraction(interactionKey)) {
      return;
    }

    perf.log('QF-FilterMenu', 'value interaction', {
      columnLabel,
      value,
      source,
    });
    toggleValue(value);
  };

  const handleValueKeyboardToggle = (event: React.KeyboardEvent, value: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleValuePointerToggle(event, value, `keyboard:${event.key}`);
  };

  const handleDisplayedValuesPointerToggle = (event: React.SyntheticEvent, source: string) => {
    event.preventDefault();
    event.stopPropagation();

    const interactionKey = 'displayed-values';
    if (shouldSkipDuplicateInteraction(interactionKey)) {
      return;
    }

    perf.log('QF-FilterMenu', 'displayed values interaction', {
      columnLabel,
      displayedValues,
      displayedCount: displayedValues.length,
      allDisplayedSelected,
      source,
    });
    handleToggleDisplayedValues();
  };

  const handleDisplayedValuesKeyboardToggle = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleDisplayedValuesPointerToggle(event, `keyboard:${event.key}`);
  };

  const handleToggleDisplayedValues = () => {
    if (allDisplayedSelected) {
      setDraftSelectedValues((previous) => {
        const next = previous.filter((value) => !displayedValues.includes(value));

        perf.log('QF-FilterMenu', 'toggle displayed values cleared', {
          columnLabel,
          displayedValues,
          nextSelectedValueCount: next.length,
          nextSelectedValues: next,
        });

        return next;
      });
      return;
    }

    setDraftSelectedValues((previous) => {
      const next = Array.from(new Set([...previous, ...displayedValues]));

      perf.log('QF-FilterMenu', 'toggle displayed values selected', {
        columnLabel,
        displayedValues,
        nextSelectedValueCount: next.length,
        nextSelectedValues: next,
      });

      return next;
    });
  };

  const handleApply = () => {
    if (!isInteractive) {
      perf.warn('QF-FilterMenu', 'apply blocked because menu is preview-only', {
        columnLabel,
        previewOpen,
        previewMode,
        mode,
      });
      return;
    }

    const trimmedSearch = valueSearch.trim();
    const shouldApplyDisplayedValues =
      mode === 'values' &&
      trimmedSearch.length > 0 &&
      draftSelectedValues.length === uniqueValues.length;
    const nextSelectedValues =
      mode !== 'values'
        ? null
        : shouldApplyDisplayedValues
          ? displayedValues
          : draftSelectedValues.length === uniqueValues.length
            ? null
            : draftSelectedValues;

    perf.log('QF-FilterMenu', 'apply clicked', {
      columnLabel,
      mode,
      previewOpen,
      previewMode,
      selectedValues: mode === 'values' ? draftSelectedValues : null,
      selectedValueCount: mode === 'values' ? draftSelectedValues.length : null,
      valueSearch: trimmedSearch,
      displayedValues: mode === 'values' ? displayedValues : null,
      applyingDisplayedValues: shouldApplyDisplayedValues,
      appliedSelectedValues: nextSelectedValues,
      conditionOperator: mode === 'condition' ? draftOperator : null,
      conditionValue: mode === 'condition' ? draftConditionValue : null,
      uniqueValues: uniqueValues.length,
    });
    if (mode === 'values') {
      onApplyValues(nextSelectedValues);
    } else {
      onApplyCondition(draftOperator, draftConditionValue);
    }
    setAnchorEl(null);
  };

  const handleClear = () => {
    if (!isInteractive) {
      perf.warn('QF-FilterMenu', 'clear blocked because menu is preview-only', {
        columnLabel,
        previewOpen,
        previewMode,
      });
      return;
    }

    perf.log('QF-FilterMenu', 'clear clicked', {
      columnLabel,
      previewOpen,
      previewMode,
    });
    onClear();
    setAnchorEl(null);
  };

  const handlePopoverClose = () => {
    if (previewOpen) {
      return;
    }

    setAnchorEl(null);
  };

  const describeInteractionTarget = (target: EventTarget | null): string => {
    if (!(target instanceof HTMLElement)) {
      return 'unknown';
    }

    const datasetValue = target.closest<HTMLElement>('[data-filter-value]')?.dataset.filterValue;
    if (datasetValue) {
      return `value:${datasetValue}`;
    }

    const text = target.textContent?.trim();
    if (text) {
      return text.slice(0, 60);
    }

    return target.tagName.toLowerCase();
  };

  const filterTrigger = (
    <IconButton
      size="small"
      ref={triggerRef as React.Ref<HTMLButtonElement> | undefined}
      data-testid={`column-filter-trigger-${filterSlug}`}
      onClick={(event) => {
        if (previewOpen) {
          return;
        }

        event.currentTarget.blur();
        setAnchorEl(event.currentTarget);
      }}
      aria-label={`Filter ${columnLabel}`}
      sx={{
        width: triggerDimension,
        height: triggerDimension,
        borderRadius: `${borderRadius.sm}px`,
        border: isInlineTrigger
          ? 'none'
          : `1px solid ${alpha(
              active || emphasizeTrigger ? theme.palette.secondary.main : theme.palette.divider,
              active || emphasizeTrigger ? 0.42 : 0.18
            )}`,
        color: active || emphasizeTrigger ? theme.palette.secondary.main : theme.palette.text.secondary,
        backgroundColor:
          active || emphasizeTrigger
            ? alpha(theme.palette.secondary.main, previewOpen ? 0.22 : 0.14)
            : isInlineTrigger
              ? 'transparent'
              : alpha(theme.palette.background.paper, 0.2),
        boxShadow: emphasizeTrigger
          ? `0 0 0 1px ${alpha(theme.palette.secondary.main, 0.24)}`
          : 'none',
        '&:hover': {
          border: isInlineTrigger ? 'none' : undefined,
          backgroundColor: alpha(
            active || emphasizeTrigger ? theme.palette.secondary.main : theme.palette.primary.main,
            active || emphasizeTrigger ? 0.24 : 0.08
          ),
        },
        '&:focus-visible': {
          border: isInlineTrigger ? 'none' : undefined,
        },
      }}
    >
      <FilterAltOutlinedIcon sx={{ fontSize: '0.9rem' }} />
    </IconButton>
  );

  return (
    <>
      {help ? (
        <ActionHelpTooltip help={help} placement="top">
          {filterTrigger}
        </ActionHelpTooltip>
      ) : (
        <Tooltip title={active ? `Filter active on ${columnLabel}` : `Filter ${columnLabel}`} placement="top">
          {filterTrigger}
        </Tooltip>
      )}

      <Popover
        open={open}
        anchorEl={resolvedAnchorEl}
        onClose={handlePopoverClose}
        keepMounted={previewOpen}
        disablePortal={previewOpen}
        sx={{ pointerEvents: open ? 'auto' : 'none' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            width: 320,
            maxWidth: 'calc(100vw - 32px)',
            p: 1.25,
            borderRadius: `${borderRadius.lg}px`,
            border: `1px solid ${surfaceBorderColor}`,
            boxShadow: `0 18px 42px ${alpha(theme.palette.common.black, 0.24)}`,
            backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
              theme.palette.background.default,
              0.94
            )} 100%)`,
          },
        }}
      >
        <Box
          ref={popoverRef}
          data-testid={`column-filter-popover-${filterSlug}`}
          onPointerDownCapture={(event) => {
            perf.log('QF-FilterMenu', 'popover pointer interaction', {
              columnLabel,
              target: describeInteractionTarget(event.target),
            });
          }}
          onClickCapture={(event) => {
            perf.log('QF-FilterMenu', 'popover click interaction', {
              columnLabel,
              target: describeInteractionTarget(event.target),
            });
          }}
        >
          <Stack spacing={layoutGap.rack}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Column options for {columnLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Sort and filter this column
            </Typography>
          </Box>

          {previewMode ? (
            <Box
              sx={{
                px: 1,
                py: 0.75,
                borderRadius: `${borderRadius.lg}px`,
                border: `1px solid ${surfaceBorderColor}`,
                backgroundColor: alpha(theme.palette.secondary.main, 0.08),
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.secondary.light, fontWeight: 700 }}>
                Tour preview only
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                Explore the layout here. Sorting and filtering stay unchanged until you leave the tour and apply a real filter.
              </Typography>
            </Box>
          ) : null}

          <Stack spacing={layoutGap.comfort}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
              Sort
            </Typography>
            <Stack direction="row" spacing={layoutGap.comfort}>
              <Button
                size="small"
                variant={sortDirection === 'asc' ? 'contained' : 'outlined'}
                onClick={() => {
                  if (!isInteractive) {
                    return;
                  }
                  onSortAscending();
                  setAnchorEl(null);
                }}
                startIcon={<SortByAlphaOutlinedIcon />}
                disabled={!isInteractive}
              >
                Sort A to Z
              </Button>
              <Button
                size="small"
                variant={sortDirection === 'desc' ? 'contained' : 'outlined'}
                onClick={() => {
                  if (!isInteractive) {
                    return;
                  }
                  onSortDescending();
                  setAnchorEl(null);
                }}
                startIcon={<ArrowDownwardOutlinedIcon />}
                disabled={!isInteractive}
              >
                Sort Z to A
              </Button>
            </Stack>
            {sortDirection ? (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  if (!isInteractive) {
                    return;
                  }
                  onClearSort();
                  setAnchorEl(null);
                }}
                sx={{ alignSelf: 'flex-start' }}
                disabled={!isInteractive}
              >
                Clear sort
              </Button>
            ) : null}
          </Stack>

          <Divider />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, value) => {
              if (value) {
                setMode(value);
              }
            }}
            aria-label={`Filter mode for ${columnLabel}`}
          >
            <ToggleButton value="values">Values</ToggleButton>
            <ToggleButton value="condition">Condition</ToggleButton>
          </ToggleButtonGroup>

          {mode === 'values' ? (
            <Stack spacing={layoutGap.row}>
              <TextField
                inputRef={valueSearchInputRef}
                size="small"
                label="Search values"
                value={valueSearch}
                onChange={(event) => setValueSearch(event.target.value)}
                inputProps={{ 'aria-label': `Search values for ${columnLabel}` }}
              />
              <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto', border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, borderRadius: `${borderRadius.md}px` }}>
                <ListItemButton
                  onPointerDownCapture={(event) => handleDisplayedValuesPointerToggle(event, 'pointerdown')}
                  onClickCapture={(event) => handleDisplayedValuesPointerToggle(event, 'click')}
                  onKeyDown={handleDisplayedValuesKeyboardToggle}
                  dense
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {allDisplayedSelected ? (
                      <CheckBoxOutlinedIcon fontSize="small" />
                    ) : someDisplayedSelected ? (
                      <IndeterminateCheckBoxOutlinedIcon fontSize="small" />
                    ) : (
                      <CheckBoxOutlineBlankOutlinedIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Select visible values"
                    primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600 } }}
                  />
                </ListItemButton>
                <Divider />
                {displayedValues.map((value) => (
                  <ListItemButton
                    key={value || '__empty__'}
                    data-filter-value={value === '' ? '(Empty)' : value}
                    onPointerDownCapture={(event) => handleValuePointerToggle(event, value, 'pointerdown')}
                    onClickCapture={(event) => handleValuePointerToggle(event, value, 'click')}
                    onKeyDown={(event) => handleValueKeyboardToggle(event, value)}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {draftSelectedValues.includes(value) ? (
                        <CheckBoxOutlinedIcon fontSize="small" />
                      ) : (
                        <CheckBoxOutlineBlankOutlinedIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={displayValue(value)}
                      secondary={`${valueCounts[value] ?? 0} row${(valueCounts[value] ?? 0) === 1 ? '' : 's'}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                ))}
                {displayedValues.length === 0 ? (
                  <Box sx={{ px: 1.5, py: 1.25 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      No values match the current search.
                    </Typography>
                  </Box>
                ) : null}
              </List>
            </Stack>
          ) : (
            <Stack spacing={layoutGap.row}>
              <TextField
                select
                size="small"
                label="Condition"
                value={draftOperator}
                onChange={(event) => setDraftOperator(event.target.value as TableFilterOperator)}
                inputProps={{ 'aria-label': `Conditional filter for ${columnLabel}` }}
              >
                {FILTER_OPERATOR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Value"
                value={draftConditionValue}
                onChange={(event) => setDraftConditionValue(event.target.value)}
                disabled={isValuelessOperator(draftOperator)}
                inputProps={{ 'aria-label': `Filter value for ${columnLabel}` }}
              />
            </Stack>
          )}

          <Divider />

          <Stack direction="row" spacing={layoutGap.row} justifyContent="space-between">
            <Button size="small" onClick={handleClear} disabled={!isInteractive}>
              Clear
            </Button>
            <Stack direction="row" spacing={layoutGap.row}>
              <Button size="small" color="inherit" onClick={handlePopoverClose} disabled={!isInteractive}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleApply} disabled={!isInteractive}>
                Apply
              </Button>
            </Stack>
          </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default ColumnFilterMenu;
